import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { itemsAPI } from '../../api/itemsAPI';

export const fetchItems = createAsyncThunk(
  'items/fetchItems',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await itemsAPI.getItems(filters);
      return response;
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        return rejectWithValue(
          'Items request timed out. Start the items API (port 8080) or set REACT_APP_ITEMS_API_URL in .env.local.'
        );
      }
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        return rejectWithValue(
          'Cannot reach items API. Start flea_platform (python manage.py runserver 8080) and set REACT_APP_ITEMS_API_URL=http://localhost:8080/api'
        );
      }
      const data = error.response?.data;
      if (typeof data === 'string') return rejectWithValue(data);
      if (data && typeof data === 'object' && data.error) return rejectWithValue(data.error);
      return rejectWithValue(error.message || 'Failed to fetch items');
    }
  }
);

// Append-mode fetch for infinite scroll. Caller passes the next page number
// alongside the same filter set; the reducer concatenates results.
export const loadMoreItems = createAsyncThunk(
  'items/loadMoreItems',
  async (filtersWithPage = {}, { rejectWithValue }) => {
    try {
      const response = await itemsAPI.getItems(filtersWithPage);
      return response;
    } catch (error) {
      const data = error.response?.data;
      if (typeof data === 'string') return rejectWithValue(data);
      if (data && typeof data === 'object' && data.error) return rejectWithValue(data.error);
      return rejectWithValue(error.message || 'Failed to load more items');
    }
  }
);

export const fetchItemById = createAsyncThunk(
  'items/fetchItemById',
  async (itemId, { rejectWithValue }) => {
    try {
      const response = await itemsAPI.getItemById(itemId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch item');
    }
  }
);

export const createItem = createAsyncThunk(
  'items/createItem',
  async (itemData, { rejectWithValue }) => {
    try {
      const response = await itemsAPI.createItem(itemData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create item');
    }
  }
);

export const createItemWithImages = createAsyncThunk(
  'items/createItemWithImages',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await itemsAPI.createItemWithImages(formData);
      return response;
    } catch (error) {
      const data = error.response?.data;
      const message =
        (typeof data === 'string' && data) ||
        (data && typeof data === 'object' && (data.error || data.message)) ||
        error.message ||
        'Failed to create item with images';
      return rejectWithValue({
        error: String(message),
        status: error.response?.status ?? null,
      });
    }
  }
);

const initialState = {
  items: [],
  currentItem: null,
  loading: false,
  loadingMore: false,
  error: null,
  filters: {
    department: '',
    category: '',
    subcategory: '',
    condition: '',
    color: '',
    size: '',
    material: '',
    length: '',
    brand: '',
    minPrice: '',
    maxPrice: '',
    search: '',
    book_subcategory: '',
  },
  pagination: {
    count: 0,
    page: 1,
    page_size: 24,
    total_pages: 1,
    has_next: false,
  },
};

const itemsSlice = createSlice({
  name: 'items',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        department: '',
        category: '',
        subcategory: '',
        condition: '',
        color: '',
        size: '',
        material: '',
        length: '',
        brand: '',
        minPrice: '',
        maxPrice: '',
        search: '',
        book_subcategory: '',
      };
    },
    clearCurrentItem: (state) => {
      state.currentItem = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Items
      .addCase(fetchItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchItems.fulfilled, (state, action) => {
        state.loading = false;
        const p = action.payload;
        if (Array.isArray(p)) {
          // Legacy array shape (kept for the brief deploy window before the
          // backend pagination change goes live).
          state.items = p;
          state.pagination = {
            count: p.length,
            page: 1,
            page_size: p.length || 24,
            total_pages: 1,
            has_next: false,
          };
        } else if (p && Array.isArray(p.results)) {
          state.items = p.results;
          state.pagination = {
            count: p.count ?? p.results.length,
            page: p.page ?? 1,
            page_size: p.page_size ?? 24,
            total_pages: p.total_pages ?? 1,
            has_next: !!p.has_next,
          };
        } else {
          state.items = [];
          state.pagination = { ...initialState.pagination };
        }
      })
      .addCase(fetchItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Load More Items (append, no flicker)
      .addCase(loadMoreItems.pending, (state) => {
        state.loadingMore = true;
      })
      .addCase(loadMoreItems.fulfilled, (state, action) => {
        state.loadingMore = false;
        const p = action.payload;
        const newRows = Array.isArray(p) ? p : Array.isArray(p?.results) ? p.results : [];
        // De-dupe by id in case of overlap on the boundary.
        const existingIds = new Set(state.items.map((i) => i.id));
        state.items = state.items.concat(newRows.filter((r) => !existingIds.has(r.id)));
        if (p && !Array.isArray(p)) {
          state.pagination = {
            count: p.count ?? state.pagination.count,
            page: p.page ?? state.pagination.page,
            page_size: p.page_size ?? state.pagination.page_size,
            total_pages: p.total_pages ?? state.pagination.total_pages,
            has_next: !!p.has_next,
          };
        }
      })
      .addCase(loadMoreItems.rejected, (state, action) => {
        state.loadingMore = false;
        state.error = action.payload;
      })
      // Fetch Item by ID
      .addCase(fetchItemById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.currentItem = null;
      })
      .addCase(fetchItemById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentItem = action.payload;
      })
      .addCase(fetchItemById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Item
      .addCase(createItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createItem.fulfilled, (state, action) => {
        state.loading = false;
        state.items.unshift(action.payload);
      })
      .addCase(createItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Item with Images
      .addCase(createItemWithImages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createItemWithImages.fulfilled, (state, action) => {
        state.loading = false;
        state.items.unshift(action.payload);
      })
      .addCase(createItemWithImages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setFilters, clearFilters, clearCurrentItem } = itemsSlice.actions;
export default itemsSlice.reducer;
